
import React, { useState } from "react";
import {
	minValue,
	TextInput,
	ArrayInput,
	SimpleFormIterator,
	NumberInput,
	BooleanInput,
	useRecordContext,
	TextField,
	Show,
	TabbedShowLayout,
	Tab,
	Datagrid,
	List,
	ShowButton,
	DeleteButton,
	Create,
	TabbedForm,
	SimpleForm,
	FormTab,
	required,
	FormDataConsumer,
	SelectArrayInput,
	usePermissions,
	TabbedShowLayoutTabs,
	useGetList,
	useDataProvider,
	useRefresh,
} from "react-admin";
import { ConfigurationsTab } from "./ra_configurations";
import { SystemsShow } from "./ra_systems";
import { ContentEditorTab, NewProjectDialog, useGithubConfig, syncProjectsConfig } from "./ra_contentEditor";
import AddIcon from "@mui/icons-material/Add";
import { Button, Box, Typography } from "@mui/material";
import { SaveButton, Toolbar, useNotify } from "react-admin";
import Viewer from "./3dViewer2";

// Toolbar that saves to Firestore then regenerates projectsConfig.json — used
// by ProjectsCreate below (project creation still goes through the plain
// react-admin form; the "New Project" button on the list uses NewProjectDialog).
const SyncSaveToolbar = () => {
	const { ghConfig } = useGithubConfig();
	const notify = useNotify();
	return (
		<Toolbar>
			<SaveButton
				label="Save & sync to site"
				mutationOptions={{
					onSuccess: async () => {
						if (!ghConfig) {
							notify('Saved, but GitHub isn\'t configured — site content was not synced.', { type: 'warning' });
							return;
						}
						try {
							await syncProjectsConfig(ghConfig);
							notify('Saved and synced — site content updated.', { type: 'success' });
						} catch (e) {
							notify(`Saved but sync failed: ${e.message}`, { type: 'warning' });
						}
					},
				}}
			/>
		</Toolbar>
	);
};

// Self-contained, in-place editable tab — same pattern as ConfigurationsTab /
// SystemsShow / ContentEditorTab: reads the record via context and saves
// directly through the dataProvider, so it works straight from the Show page
// without needing a separate Edit route.
const ProjectDetailsTab = () => {
	const record = useRecordContext();
	const { permissions } = usePermissions();
	const dataProvider = useDataProvider();
	const refresh = useRefresh();
	const notify = useNotify();
	const { ghConfig } = useGithubConfig();
	const { data: servicesData } = useGetList('services', {
		pagination: { page: 1, perPage: 100 },
		sort: { field: 'order', order: 'ASC' },
	});
	const serviceChoices = (servicesData ?? []).map(s => ({ id: s.id, name: s.name }));

	if (permissions < 98) {
		return (
			<Box sx={{ p: 2 }}>
				<Typography variant="h6">{record.name}</Typography>
				<Typography color="text.secondary" sx={{ whiteSpace: 'pre-wrap', my: 1 }}>{record.description}</Typography>
				<Typography variant="body2" color="text.secondary">Year: {record.year}</Typography>
			</Box>
		);
	}

	const handleSubmit = async (values) => {
		try {
			await dataProvider.update('projects', { id: record.id, data: values, previousData: record });
			if (ghConfig) {
				try {
					await syncProjectsConfig(ghConfig);
					notify('Saved and synced — site content updated.', { type: 'success' });
				} catch (e) {
					notify(`Saved but sync failed: ${e.message}`, { type: 'warning' });
				}
			} else {
				notify('Saved, but GitHub isn\'t configured — site content was not synced.', { type: 'warning' });
			}
			refresh();
		} catch (e) {
			notify(`Save failed: ${e.message}`, { type: 'error' });
		}
	};

	return (
		<SimpleForm record={record} onSubmit={handleSubmit} toolbar={<Toolbar><SaveButton label="Save & sync to site" /></Toolbar>}>
			<TextInput source="name" label="Project Name" fullWidth />
			<TextInput source="description" label="Description" multiline fullWidth />
			<TextInput source="nameid" label="Project Nameid" fullWidth />
			<NumberInput source="year" label="Year" validate={[required(), minValue(1900)]} max={new Date().getFullYear()} fullWidth />
			<BooleanInput source="visibility" label="Visible on site" defaultValue={true} />
			<SelectArrayInput source="services" label="Services" choices={serviceChoices}
				helperText="Also picks the badge icon shown on the project card (first service selected)." />
			<TextInput source="markdownFile" label="Markdown file (e.g. classe950.md)" fullWidth />
			<TextInput source="model_full" label="3D Model filename" fullWidth
				helperText="Leave blank to hide the 3D viewer on this project's page entirely." />
		</SimpleForm>
	);
};

export const ProjectShow = (props) => {
	const { permissions } = usePermissions();

	return (
		<Show {...props} title="Project Details">
			<TabbedShowLayout
                tabs={
                    <TabbedShowLayoutTabs
                        variant="scrollable"
                        scrollButtons="auto"   // shows arrows if needed
                        allowScrollButtonsMobile // (MUI v5) enable on mobile
                    />
                }
            >
				{/* Project Details Tab — full editable form, in place */}
				<Tab label="Project Details">
					<ProjectDetailsTab />
				</Tab>

				{/* Configurations Tab */}
				{permissions >= 98 && (
					<Tab label="Configurations">
						<ConfigurationsTab />
					</Tab>
				)}

				{permissions >= 98 && (
					<Tab label="Systems">
						<SystemsShow />
					</Tab>
				)}

				{permissions >= 98 && (
					<Tab label="Content">
						<ContentEditorTab />
					</Tab>
				)}

				{/* Systems Tab */}
				<Tab label="3dModel">{true && <ModelView />}</Tab>
				{/* Add more tabs as needed */}
			</TabbedShowLayout>
		</Show>
	);
};


export const ProjectsList = (props) => {
	const { permissions } = usePermissions();
	const [newProjectOpen, setNewProjectOpen] = useState(false);

	return (
		<>
			<List
				{...props}
				title="Projects"
				actions={
					permissions >= 98 ? (
						<Button
							variant="contained"
							size="small"
							startIcon={<AddIcon />}
							onClick={() => setNewProjectOpen(true)}
							sx={{ m: 1 }}
						>
							New Project
						</Button>
					) : undefined
				}
			>
				<Datagrid rowClick="show">
					<TextField source="name" label="Project Name" />
					<TextField source="year" label="Year" />
					<ShowButton />
					{permissions >= 99 && <DeleteButton />}
				</Datagrid>
			</List>
			<NewProjectDialog open={newProjectOpen} onClose={() => setNewProjectOpen(false)} />
		</>
	);
};
export const ProjectsCreate = (props) => {
	const { data: servicesData } = useGetList('services', {
		pagination: { page: 1, perPage: 100 },
		sort: { field: 'order', order: 'ASC' },
	});
	const serviceChoices = (servicesData ?? []).map(s => ({ id: s.id, name: s.name }));

	return (
	<Create {...props} title="Create Project">
		<TabbedForm toolbar={<SyncSaveToolbar />}>
			{/* Project Details Tab */}
			<FormTab label="Project Details">
				<TextInput source="name" label="Project Name" validate={required()} fullWidth />
				<TextInput source="nameid" label="Project ID (nameid)" validate={required()} fullWidth />
				<NumberInput source="year" label="Year" validate={[required(), minValue(1900)]} max={new Date().getFullYear()} fullWidth />
				<TextInput source="description" label="Description" multiline fullWidth />
				<BooleanInput source="visibility" label="Visible on site" defaultValue={true} />
				<SelectArrayInput source="services" label="Services" choices={serviceChoices} />
				<TextInput source="markdownFile" label="Markdown file" fullWidth />
				<TextInput source="model_full" label="3D Model filename" fullWidth
					helperText="Leave blank to hide the 3D viewer on this project's page entirely." />
			</FormTab>

			{/* Systems Tab */}
			<FormTab label="Systems">
				<ArrayInput source="systems">
					<SimpleFormIterator>
						<TextInput
							source="systemName"
							label="System Name"
							validate={required()}
							fullWidth
						/>
						<NumberInput
							source="massMultiplier"
							label="Mass Multiplier"
							defaultValue={1}
							validate={[required(), minValue(0)]}
						/>
						<BooleanInput
							source="required"
							label="Required"
							defaultValue={false}
						/>
						{/* Initialize sum-up fields */}
						<NumberInput
							source="sumup.sumup_mass"
							label="Total Mass"
							defaultValue={0}
							disabled
						/>
						<NumberInput
							source="sumup.sumup_price"
							label="Total Price"
							defaultValue={0}
							disabled
						/>
						{/* Other system fields */}
					</SimpleFormIterator>
				</ArrayInput>
			</FormTab>

			{/* Configurations Tab */}
			<FormTab label="Configurations">
				<ArrayInput source="configurations">
					<SimpleFormIterator>
						{/* Fields for configuration */}
						<TextInput
							source="configName"
							label="Configuration Name"
							validate={required()}
							fullWidth
						/>
						<TextInput
							source="description"
							label="Description"
							multiline
							fullWidth
						/>
						{/* Initialize sum-up fields */}
						<NumberInput
							source="sumup.sumup_mass"
							label="Total Mass"
							defaultValue={0}
							disabled
						/>
						<NumberInput
							source="sumup.sumup_price"
							label="Total Price"
							defaultValue={0}
							disabled
						/>

						{/* Systems Selection */}
						<FormDataConsumer>
							{({ formData }) => {
								const systems = formData.systems || [];
								const choices = systems.map((system, index) => ({
									id: index,
									name: system.systemName || `System ${index + 1}`,
								}));
								return (
									<SelectArrayInput
										label="Systems"
										source="systemIndexes"
										choices={choices}
										optionText="name"
										optionValue="id"
									/>
								);
							}}
						</FormDataConsumer>
					</SimpleFormIterator>
				</ArrayInput>
			</FormTab>

			{/* Remove Authorized Users Tab */}
			{/* Add more tabs as needed */}
		</TabbedForm>
	</Create>
	);
};

export const ModelView = () => {
	const record = useRecordContext(); // The project or system containing the items
	if (record.nameid && record.model_full) {
		const model = `${process.env.PUBLIC_URL}/content/projects/${record.nameid}/3dModels/${record.model_full}`;
		return (
			<div
				style={{
					height: "100%",
					width: "100%",
				}}
			>
				{model && (
					<Viewer
						modelUrl={model}
						admin={true}
                        modelOptionsFileName={record.model_full + "_modelOptions"}
						style={{
							minHeight: "60vh",
							height: "calc(100vh - 200px)",
							width: "100%",
						}}
					/>
				)}
			</div>
		);
	}
	return <></>;
};
